import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function ImportResult({ r }) {
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 p-3 text-sm space-y-1" data-testid="import-result">
      <div><span className="text-teal-300 font-bold">{r.created}</span> added · <span className="text-amber-300 font-bold">{r.updated}</span> updated · <span className="text-rose-300 font-bold">{r.skipped}</span> skipped</div>
      {r.errors.length > 0 && (
        <ul className="max-h-32 overflow-y-auto text-xs text-rose-300 list-disc pl-4" data-testid="import-errors">
          {r.errors.map((e) => <li key={e.row}>Row {e.row}: {e.reason}</li>)}
        </ul>
      )}
      {r.warnings?.length > 0 && (
        <ul className="max-h-24 overflow-y-auto text-xs text-amber-300 list-disc pl-4" data-testid="import-warnings">
          {r.warnings.map((w) => <li key={w.row}>Row {w.row}: {w.reason}</li>)}
        </ul>
      )}
    </div>
  );
}

export function ImportGuestsModal({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const close = () => { setFile(null); setResult(null); onClose(); };
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/guests/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Import done: ${data.created} added, ${data.updated} updated`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#131D33] border-slate-700 text-slate-100 max-w-lg" data-testid="import-guests-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import Guests</DialogTitle>
          <DialogDescription className="text-slate-400">
            Upload an Excel (.xlsx) or CSV file. Columns: First Name, Last Name, Email, Phone, Ticket Type (Regular, VIP, Employee, Sponsor, General), Notes. Existing guests (same email, or same name if no email) are updated.
          </DialogDescription>
        </DialogHeader>
        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-600 hover:border-orange-500 rounded-xl p-6 cursor-pointer transition-colors" data-testid="import-dropzone">
          <FileSpreadsheet className="text-orange-400" size={32} />
          <span className="text-sm text-slate-300">{file ? file.name : "Choose a .xlsx or .csv file"}</span>
          <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-file-input" />
        </label>
        {result && <ImportResult r={result} />}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={close} className="bg-slate-900 border-slate-700" data-testid="import-close-btn">Close</Button>
          <Button onClick={upload} disabled={!file || busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="import-submit-btn">{busy ? "Importing…" : "Import"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
